import { z } from 'zod'
import {
  ChangeId,
  EndDate,
  ObjectId,
  ParamTypeId,
  StartDate,
  UpdateDate,
} from './common-schemas'
import { GarItem } from './gar-item'

abstract class Param<T extends string> extends GarItem<T, ParamData> {
  protected get schema() {
    return SCHEMA
  }
}

/** @description Information about parameter of address-forming element */
export class AddressObjectParam extends Param<'AddressObjectParam'> {
  readonly type = 'AddressObjectParam'
}

/** @description Information about house parameter */
export class HouseParam extends Param<'HouseParam'> {
  readonly type = 'HouseParam'
}

/** @description Information about apartment parameter */
export class ApartmentParam extends Param<'ApartmentParam'> {
  readonly type = 'ApartmentParam'
}

/** @description Information about room parameter */
export class RoomParam extends Param<'RoomParam'> {
  readonly type = 'RoomParam'
}

/** @description Information about land plot parameter */
export class SteadParam extends Param<'SteadParam'> {
  readonly type = 'SteadParam'
}

/** @description Information about car place parameter */
export class CarPlaceParam extends Param<'CarPlaceParam'> {
  readonly type = 'CarPlaceParam'
}

const SCHEMA: GarItem.Schema<ParamData> = z
  .strictObject({
    ID: z.coerce.number<string>().int().describe('Record identifier'),

    OBJECTID: ObjectId,

    CHANGEID: ChangeId.optional(),

    CHANGEIDEND: ChangeId.optional(),

    TYPEID: ParamTypeId,

    VALUE: z.string().describe('Parameter value'),

    UPDATEDATE: UpdateDate,

    STARTDATE: StartDate,

    ENDDATE: EndDate,
  })
  .transform<ParamData>(attributes => ({
    id: attributes.ID,
    objectId: attributes.OBJECTID,
    changeId: attributes.CHANGEID,
    changeIdEnd: attributes.CHANGEIDEND,
    typeId: attributes.TYPEID,
    value: attributes.VALUE,
    updateDate: attributes.UPDATEDATE,
    startDate: attributes.STARTDATE,
    endDate: attributes.ENDDATE,
  }))

export interface ParamData {
  /** @description Record identifier */
  readonly id: number

  /** @description Global unique identifier of the address object */
  readonly objectId: number

  /** @description Identifier of the changing transaction */
  readonly changeId?: number

  /** @description Identifier of the completing transaction */
  readonly changeIdEnd?: number

  /** @description Parameter type identifier */
  readonly typeId: number

  /** @description Parameter value */
  readonly value: string

  /** @description Date when the record was last updated */
  readonly updateDate: Date

  /** @description Date when the record became valid */
  readonly startDate: Date

  /** @description Date when the record is valid until */
  readonly endDate: Date
}
